import React from 'react';
import { CertQuestionMatchingDrag } from '@unity-learning/shared';
import { useDrag, useDrop, DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { ContextCode } from './ContextCode';

interface QuestionMatchingDragProps {
    question: Omit<CertQuestionMatchingDrag, 'validation'>;
    value: Record<string, string>;
    onChange: (value: Record<string, string>) => void;
    disabled?: boolean;
}

interface DragItem {
    id: string;
    type: string;
}

const ItemTypes = {
    SOURCE: 'source'
};

// Draggable source pill
const SourcePill: React.FC<{
    id: string;
    value: string;
    disabled: boolean;
    isUsed: boolean;
}> = ({ id, value, disabled, isUsed }) => {
    const [{ isDragging }, drag] = useDrag(() => ({
        type: ItemTypes.SOURCE,
        item: { id, type: ItemTypes.SOURCE },
        canDrag: !disabled && !isUsed,
        collect: (monitor) => ({
            isDragging: monitor.isDragging()
        })
    }), [disabled, isUsed]);

    return (
        <div
            ref={drag}
            className={`
        px-4 py-2 rounded-lg font-mono text-lg font-bold text-center
        transition-all duration-200 select-none
        ${isUsed
                    ? 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed opacity-50'
                    : isDragging
                        ? 'bg-blue-400 text-white opacity-75 cursor-grabbing'
                        : 'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300 cursor-grab hover:bg-blue-200 dark:hover:bg-blue-900'
                }
        ${disabled ? 'cursor-not-allowed' : ''}
      `}
        >
            {value}
        </div>
    );
};

// Target drop well
const TargetWell: React.FC<{
    targetId: string;
    targetText: string;
    droppedSourceId: string | null;
    sources: { id: string; value: string }[];
    onDrop: (targetId: string, sourceId: string) => void;
    onRemove: (targetId: string) => void;
    disabled: boolean;
}> = ({ targetId, targetText, droppedSourceId, sources, onDrop, onRemove, disabled }) => {
    const [{ isOver, canDrop }, drop] = useDrop(() => ({
        accept: ItemTypes.SOURCE,
        drop: (item: DragItem) => {
            onDrop(targetId, item.id);
        },
        canDrop: () => !disabled,
        collect: (monitor) => ({
            isOver: monitor.isOver(),
            canDrop: monitor.canDrop()
        })
    }), [targetId, disabled, onDrop]);

    const droppedSource = sources.find(s => s.id === droppedSourceId);

    return (
        <div className="flex items-center gap-4 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
            {/* Drop Well */}
            <div
                ref={drop}
                className={`
          min-w-[100px] h-12 px-4 rounded-lg border-2 border-dashed
          flex items-center justify-center
          transition-all duration-200
          ${isOver && canDrop
                        ? 'border-blue-500 bg-blue-100 dark:bg-blue-900/30'
                        : droppedSource
                            ? 'border-green-400 bg-green-50 dark:bg-green-900/30'
                            : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900'
                    }
        `}
            >
                {droppedSource ? (
                    <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-green-700 dark:text-green-400">
                            {droppedSource.value}
                        </span>
                        {!disabled && (
                            <button
                                type="button"
                                onClick={() => onRemove(targetId)}
                                title="Retirer"
                                aria-label="Retirer cet élément"
                                className="text-red-500 hover:text-red-700 text-sm"
                            >
                                ✕
                            </button>
                        )}
                    </div>
                ) : (
                    <span className="text-gray-400 text-sm">Déposez ici</span>
                )}
            </div>

            {/* Target Text */}
            <div className="flex-1 text-gray-700 dark:text-gray-300">
                {targetText}
            </div>
        </div>
    );
};

// Main component
const QuestionMatchingDragInner: React.FC<QuestionMatchingDragProps> = ({
    question,
    value,
    onChange,
    disabled = false
}) => {
    const { content } = question;
    const { sources, targets, context_code } = content;

    // Get list of used source IDs
    const usedSourceIds = Object.values(value);

    const handleDrop = (targetId: string, sourceId: string) => {
        // If the sourceId is already placed somewhere else, remove it first
        const newValue = { ...value };
        for (const [tid, sid] of Object.entries(newValue)) {
            if (sid === sourceId) {
                delete newValue[tid];
            }
        }
        // If the target already has a source, do nothing (swap not implemented, user clicks X first)
        if (!newValue[targetId]) {
            newValue[targetId] = sourceId;
        }
        onChange(newValue);
    };

    const handleRemove = (targetId: string) => {
        const newValue = { ...value };
        delete newValue[targetId];
        onChange(newValue);
    };

    return (
        <div className="space-y-6">
            {context_code && <ContextCode code={context_code} />}
            <div className="grid grid-cols-2 gap-8">
                {/* Source Column */}
                <div className="space-y-3">
                    <div className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                        Glissez les éléments depuis ici :
                    </div>
                    <div className="space-y-2">
                        {sources.map((source) => (
                            <SourcePill
                                key={source.id}
                                id={source.id}
                                value={source.value}
                                disabled={disabled}
                                isUsed={usedSourceIds.includes(source.id)}
                            />
                        ))}
                    </div>
                </div>

                {/* Target Column */}
                <div className="space-y-3">
                    <div className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                        Déposez-les en face de leur correspondance :
                    </div>
                    <div className="space-y-2">
                        {targets.map((target) => (
                            <TargetWell
                                key={target.id}
                                targetId={target.id}
                                targetText={(target as any).text || target.value}
                                droppedSourceId={value[target.id] || null}
                                sources={sources.map(s => ({ id: s.id, value: s.value }))}
                                onDrop={handleDrop}
                                onRemove={handleRemove}
                                disabled={disabled}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

// Wrapper with DnD Provider
export const QuestionMatchingDrag: React.FC<QuestionMatchingDragProps> = (props) => {
    return (
        <DndProvider backend={HTML5Backend}>
            <QuestionMatchingDragInner {...props} />
        </DndProvider>
    );
};

export default QuestionMatchingDrag;
